import { z } from "zod";
import { DISPLAY_CATEGORIES } from "./model-categories.js";
import { PATTERN_LIST_MIN_LENGTH } from "./tier-limits.js";

// Hoisted up the file so AnonymizeRequest (below) can reference it.
// Phase 3.4 (2026-06-26): the `redacted` mode (single user-typed marker for ALL
// spans, e.g. `[REDACTED]` everywhere) was removed entirely. It conceptually
// overlapped with `custom` (per-category labels), and the activation-via-toggle
// pattern introduced by Phase 3.3 fought with the picker over the same
// `markerFormat` field. Picker now selects `custom` directly (rendered as
// "Custom [LABEL]"). See docs/superpowers/specs/2026-06-26-pre-creem-roadmap.md
// §3.4 for the rationale.
export const MarkerFormat = z.enum(["default", "custom", "xxxxx", "blackbar", "pseudonym"]);
export type MarkerFormat = z.infer<typeof MarkerFormat>;

/**
 * How `uncertainSpans` (the UNKLAR/ambiguous layer) render into the
 * `anonymized` string. `uncertainSpans` itself is always returned with
 * offsets and scores, in all three modes — this only controls what happens
 * to the text at those offsets in `anonymized`.
 *
 * - `redact` (default): uncertain spans render as `[UNKLAR_n]`, same as any
 *   other marker. In masked formats (xxxxx/blackbar) they're masked exactly
 *   like a real span — no distinguishing treatment, since the whole point of
 *   those formats is that categories aren't visible anyway. This is the
 *   privacy-safe default: nothing the model flagged, even at low confidence,
 *   reaches the caller as plaintext.
 * - `keep`: legacy behavior. Uncertain spans are left as plaintext in
 *   `anonymized`, unmarked and unmasked.
 * - `mark`: content-preserving disclosure. Uncertain spans render as
 *   `[UNKLAR_n: original]` — the marker plus the original text inline — so a
 *   reviewer can see what was flagged without leaving the redacted view.
 */
export const AmbiguousMode = z.enum(["redact", "keep", "mark"]);
export type AmbiguousMode = z.infer<typeof AmbiguousMode>;

/**
 * How an input's structure is treated for the purpose of preserving JSON/CSV
 * format instead of running the line-for-line prose rewrite (Plan 07,
 * apps/api/src/lib/structure/). "auto" (the default) detects JSON or CSV on
 * the server (apps/api/src/lib/structure/detect.ts) and otherwise falls back
 * to "text"; "text"/"json"/"csv" force a specific path. A caller can name
 * "json" or "csv" explicitly and still get "text" back — a scanner failure
 * on malformed input, a non-"auto" mismatch, and every caller below Docs
 * (structure-preserving output is a paid feature; recognition itself is not
 * — see the /anonymize route handler) all fall back silently rather than
 * erroring. `AnonymizeStats.structure` in the response reports what actually
 * ran, which can differ from what this field asked for.
 */
export const Structure = z.enum(["auto", "text", "json", "csv"]);
export type Structure = z.infer<typeof Structure>;

/**
 * The display categories a paid caller may ask us to DETECT but not redact
 * (plan 05). Deliberately a two-value enum rather than `DisplayCategory`
 * narrowed at the route: PERSON, EMAIL, PHONE, ADDRESS, ACCOUNT and SECRET
 * are the categories the product exists to remove, and a request that could
 * name them would make "keep" a general opt-out of redaction. DATE and URL
 * are the two that are most often structure rather than PII in everyday
 * text - a chat timestamp, a ticket link - which is the whole reason they
 * are offered.
 *
 * Recognition is unaffected on every tier: `keep` only suppresses spans
 * AFTER detection, and only for callers who are entitled to it. A caller
 * below Pro that sends this field is REJECTED (402, `field: "keep"`), never
 * silently ignored - a dropped field looks like a bug from the outside.
 */
export const KeepableCategory = z.enum(["DATE", "URL"]);
export type KeepableCategory = z.infer<typeof KeepableCategory>;

/**
 * Shared by `AnonymizeRequest`, `ScanRequest` and the `keep` multipart field
 * on /anonymize-file, so all three reject the identical value set. `.max(2)`
 * is a bound on the request, not a statement about the feature: it happens
 * to equal the enum's size today, which makes a duplicate-free list the only
 * way to reach it. Duplicates are tolerated rather than rejected - the
 * server dedupes into a Set before use, so `["DATE","DATE"]` and `["DATE"]`
 * mean the same thing, and failing the request over it would buy nothing.
 */
const KeepList = z.array(KeepableCategory).max(2);

export const AnonymizeRequest = z.object({
  // Zod cap is a hard ceiling well above any tier's char limit.
  // Per-tier enforcement happens in the /anonymize route handler.
  text: z.string().min(1).max(1_500_000),
  /**
   * Optional marker render format. When omitted, the server falls back to the
   * caller's user-profile `markerFormat`, then to the tier default. Tier
   * gating (xxxxx/blackbar on Free; `default` on any paid tier; `custom` on
   * Pro/Ultra) is enforced in the route handler. Mismatch -> 402 with code
   * `tier_required`.
   */
  markerFormat: MarkerFormat.optional(),
  /**
   * Cloudflare Turnstile token. Required for anonymous (unauthenticated)
   * requests unless a valid `unpii_anon_verified` cookie is present.
   * Signed-in users and API-key callers ignore this field.
   */
  turnstile_token: z.string().optional(),
  /**
   * How `uncertainSpans` render into `anonymized`. See `AmbiguousMode`'s
   * docstring for what each value does. Defaults to `redact` — the
   * uncertain/ambiguous bucket used to render as plaintext for every caller,
   * which was a leak; `redact` closes it. `.default()` means this field is
   * required on the parsed (output) type but stays optional on the wire, so
   * older/generic callers that omit it get the safe default for free.
   */
  ambiguous: AmbiguousMode.default("redact"),
  /**
   * Per-request label override, keyed by display category. It lets an API
   * caller rename AMBIGUOUS_LABEL ("UNKLAR"); the web client sends nothing
   * here - UNKLAR is a name in every UI language (owner, 2026-09-15; the EN
   * client sent `{ UNKLAR: "UNCLEAR" }` until then). See `labelForCategory`
   * in shared/render-markers.ts. Same shape as
   * `UserPublic`/`UserUpdate`'s account-level `customMarkerMap` (the paid
   * "custom" format per-category labels, DB-stored, unrelated lifecycle) —
   * deliberately not a new schema. The route handler only ever honors the
   * AMBIGUOUS_LABEL key from THIS field outside "custom" format; any other
   * key a caller sends here is ignored server-side, so this field can't be
   * used to smuggle a per-category override the account's own custom-label
   * feature doesn't grant.
   */
  customMarkerMap: z.record(z.string(), z.string()).optional(),
  /**
   * Requests JSON/CSV structure preservation instead of the line-for-line
   * prose rewrite. See `Structure`'s docstring for what each value does.
   * Defaults to "auto" — like `ambiguous`, `.default()` means this field is
   * optional on the wire and always present on the parsed (output) type, so
   * older/generic callers that omit it get detection for free. Below Docs
   * the route handler forces the effective value to "text" regardless of
   * what is sent here — recognition runs for every tier, format-preserving
   * output is a Docs+ feature — and never rejects a Free caller's explicit
   * "json"/"csv" request with an error. `stats.structure` in the response
   * always reports what actually ran.
   */
  structure: Structure.default("auto"),
  /**
   * Categories to DETECT but leave as plain text (plan 05). See
   * `KeepableCategory` for which values exist and why only those. Optional
   * rather than `.default([])`: absent and `[]` mean the same thing to the
   * server, and keeping it optional means a body that never mentions the
   * field is byte-identical on the wire to one from before this field
   * existed.
   *
   * Pro/Ultra only, enforced in `prepareAndRun` BEFORE the rate-limit check
   * so a rejected request costs no budget. Unlike `structure` and
   * `markerFormat`, an unentitled caller is REJECTED (402, `field: "keep"`)
   * rather than silently downgraded - see `KeepableCategory`'s docstring.
   */
  keep: KeepList.optional(),
  /**
   * Per-request opt-out of the caller's OWN saved custom redaction rules
   * (2026-09-16, "Erlauben/Regel direkt aus dem Ergebnis"). Absent or `true`
   * (the default) is today's behavior: the account's rules load and apply
   * exactly as before. `false` skips loading rules for THIS request only —
   * the stored rows are untouched, and the next request without the flag
   * sees them again. No tier gate: below Docs no rules are ever loaded
   * regardless of this field (see the loading block in
   * apps/api/src/lib/anonymize-request.ts), so there is nothing for it to
   * turn off there. Not available on `/anonymize-file`, which has no
   * equivalent field.
   */
  applyCustomRules: z.boolean().optional(),
  /**
   * Per-request opt-out of the caller's OWN saved allowlist — same
   * semantics as `applyCustomRules` just above, for the allowlist instead
   * of custom rules. Independent of it: either, both, or neither may be set
   * `false` on the same request.
   */
  applyAllowlist: z.boolean().optional(),
});
/**
 * The wire (input) shape, not the parsed/output shape — `ambiguous` stays
 * optional here because `.default()` only fills it in during `.parse()`;
 * `z.infer` would make it required and break every caller that (correctly)
 * omits it. Route handlers get the fully-defaulted shape for free from
 * `AnonymizeRequest.safeParse(...).data`, which is typed independently of
 * this alias.
 */
export type AnonymizeRequest = z.input<typeof AnonymizeRequest>;

/**
 * Where a span came from in the detection pipeline (Task T1, plan 03).
 * Additive-only: lets the frontend and API customers tell a model detection
 * apart from a deterministic rule, a coreference/mention-propagation follow-
 * up, a gazetteer list-hit, a structural greeting/signoff anchor, or the
 * caller's own custom rule.
 *
 * `name` is populated for exactly two kinds:
 *  - `rule`: the internal heuristic's short name (e.g. "iban", "chat_speaker")
 *    — derived from its `HEURISTIC_*` rawLabel, never a user-facing string.
 *  - `custom_rule`: the caller's OWN rule name (`CustomRedactionRule.name`).
 *    This goes back to the SAME account that defined the rule; it is never
 *    logged (AGENTS.md §1 — no span text, no rule content, in logs).
 * Every other kind omits `name` entirely.
 */
export const SpanSource = z.object({
  kind: z.enum([
    "model",
    "rule",
    "custom_rule",
    "coreference",
    "propagation",
    "gazetteer",
    "anchor",
  ]),
  name: z.string().max(80).optional(),
});
export type SpanSource = z.infer<typeof SpanSource>;

export const Span = z.object({
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
  category: z.string(),
  id: z.number().int().positive(),
  original: z.string(),
  // Optional for backwards compatibility. Backend (apps/api/src/llm.ts)
  // populates this via toPublicSpans(). Frontend treats a missing score as
  // "no confidence info" and renders the marker without the confidence badge.
  score: z.number().min(0).max(1).optional(),
  // Optional (Task T1, plan 03) so older/generic clients and every existing
  // test keep parsing unchanged. Populated by apps/api/src/llm.ts at each
  // span-creation point (see toPublicSpans) — absent means an intermediate
  // pipeline step created the span without an explicit tag (e.g. the plain
  // honorific-anchor insert), not that the field was stripped.
  source: SpanSource.optional(),
});
export type Span = z.infer<typeof Span>;

export const AnonymizeStats = z.object({
  request_id: z.string(),
  processed_chars: z.number().int().nonnegative(),
  /**
   * Class that served the inference call. `gpu` is the natural class for
   * Pro/Ultra; `fast` for Docs/API; `slow` for Free. When GPU is degraded,
   * Pro/Ultra calls show `fast` here so the client can render a degraded
   * banner without a separate /health poll.
   */
  servedBy: z.enum(["slow", "fast", "gpu"]),
  /**
   * Distinct markers per category over `spans` (Task T2, plan 03) — the same
   * numbering the `anonymized` string's markers carry, so the same person
   * mentioned three times counts once. Computed by
   * `shared/render-markers.ts`'s `countMarkers`, the identical function the
   * web client uses for its on-screen count line, so the two can never drift
   * apart. For anon and free callers this holds exactly one key, `REDACTED`
   * — those tiers never receive real categories.
   */
  counts: z.record(z.string(), z.number().int().nonnegative()),
  /**
   * Distinct markers over `uncertainSpans`, same counting rule as `counts`
   * (a single total, not a per-category breakdown).
   */
  uncertainCount: z.number().int().nonnegative(),
  /**
   * Structure handling that ACTUALLY ran (Plan 07) — see `Structure`'s
   * docstring. Never "auto": this reports the resolved path, not the
   * request. May differ from what `AnonymizeRequest.structure` asked for.
   */
  structure: z.enum(["text", "json", "csv"]),
  /**
   * Categories that were detected and deliberately NOT redacted, because the
   * request asked for them via `keep` (plan 05). Present only when at least
   * one category was actually kept; absent means nothing was, which is why
   * this needs no empty-array case and why every response recorded before
   * this field existed still parses.
   *
   * It exists because the rest of this object goes quiet about them: a kept
   * span appears in neither `spans` nor `uncertainSpans` nor `counts`, so
   * without this field a reader could not tell "no dates in the text" from
   * "dates left in the text on purpose".
   */
  kept: z.array(KeepableCategory).optional(),
});
export type AnonymizeStats = z.infer<typeof AnonymizeStats>;

/**
 * A signed, third-party-verifiable proof that the `outputSha256` below was
 * produced by unpii's anonymization pipeline (Task T4, plan 03). Ed25519,
 * deliberately not HMAC: the whole point is that a Docs customer can hand
 * this receipt to someone else, who checks it against our published public
 * key (`GET /api/v1/receipts/public-key`) without ever asking us to
 * confirm anything. An HMAC is only verifiable by whoever holds the shared
 * secret — that would be us, which makes it useless as a THIRD-party
 * proof.
 *
 * Present only from Docs tier upward — `null` on every anon/free response
 * (see the route handlers in apps/api/src/routes/anonymize.ts,
 * anonymize-file.ts, and scan.ts).
 *
 * The receipt bears witness to the OUTPUT this response hands back, never
 * to the input we were sent — Charter §1: we don't want to have seen the
 * original text well enough to prove anything about it, only about what we
 * produced. `outputSha256` is the sha256 (lowercase hex) over exactly what
 * THIS response carries as its payload, and that differs by endpoint —
 * read the field's own docstring below rather than assuming one universal
 * rule; the four response shapes below hand the payload back differently.
 * Notably, `/scan` (Task T5, plan 03) has no rendered text at all —
 * `ScanResponse` never returns `anonymized` — so its `outputSha256` covers
 * `canonicalJson({ spans, uncertainSpans })` of the tier-masked public spans
 * it DOES return, not a string. See `ScanResponse`'s own docstring.
 *
 * `model` is a public speak-name (`apps/api/src/lib/public-model-name.ts`)
 * — never a Hugging Face Hub path, never "openmed", never "finetune".
 *
 * `rulesetHash` covers the caller's OWN custom redaction rules as actually
 * applied to this request — never their names (a rename doesn't change
 * what gets redacted) and never a pattern in the clear, only a hash of it.
 * `null` when no custom rule fired. It deliberately does NOT cover the
 * allowlist, which also changes the output — a reader must not read a
 * matching `rulesetHash` as "nothing about this request's rule
 * configuration differs", only that the ruleset half doesn't. See
 * `apps/api/src/lib/receipt.ts`'s `rulesetHash` for exactly what goes in.
 *
 * `keyId` names which signing key produced `signature` — the first 16 hex
 * characters of the sha256 of the public key's DER encoding
 * (`apps/api/src/lib/receipt.ts`'s `keyIdFor`). `signature` is base64url
 * over `canonicalJson` of every field above except `signature` itself —
 * see `canonical-json.ts` and `receipt.ts`'s `verifyReceipt`, which is
 * written to be reproducible by a third party with no dependency on this
 * package.
 */
export const Receipt = z.object({
  version: z.literal(1),
  issuedAt: z.string(),
  requestId: z.string(),
  model: z.string(),
  processedChars: z.number().int().nonnegative(),
  counts: z.record(z.string(), z.number().int().nonnegative()),
  uncertainCount: z.number().int().nonnegative(),
  outputSha256: z.string(),
  rulesetHash: z.string().nullable(),
  keyId: z.string(),
  signature: z.string(),
});
export type Receipt = z.infer<typeof Receipt>;

export const AnonymizeResponse = z.object({
  anonymized: z.string(),
  /** Auto-redacted spans, reflected in `anonymized` text. */
  spans: z.array(Span),
  /**
   * Low-confidence model emissions surfaced as user-facing hints.
   * NOT reflected in `anonymized` — the UI overlays them as suggestions.
   * Always present (possibly empty). IDs are per-category 1..N preview
   * numbers, independent of `spans`. See
   * docs/superpowers/specs/2026-05-23-uncertain-spans.md.
   */
  uncertainSpans: z.array(Span),
  stats: AnonymizeStats,
  /**
   * See `Receipt`'s own docstring. Here, `outputSha256` is the sha256 over
   * the `anonymized` string above, UTF-8 encoded — that string IS this
   * endpoint's output.
   */
  receipt: Receipt.nullable(),
});
export type AnonymizeResponse = z.infer<typeof AnonymizeResponse>;

/**
 * Request body of POST /api/v1/scan (Task T5, plan 03). Deliberately a
 * SUBSET of `AnonymizeRequest`'s fields: no `markerFormat`, no `ambiguous`,
 * no `customMarkerMap` — /scan never renders or returns text, so none of the
 * three fields that only affect the shape of a rendered `anonymized` string
 * have anything to act on here. Same `text` bound as `AnonymizeRequest` (see
 * that schema's own comment) — per-tier enforcement happens in the route
 * handler, same as /anonymize.
 */
export const ScanRequest = z.object({
  text: z.string().min(1).max(1_500_000),
  /**
   * Cloudflare Turnstile token. Same semantics as
   * `AnonymizeRequest.turnstile_token` — required for anonymous
   * (unauthenticated) requests unless a valid `unpii_anon_verified` cookie
   * is present. Signed-in users and API-key callers ignore this field.
   */
  turnstile_token: z.string().optional(),
  /**
   * Same field, same enum and same Pro+ gate as
   * `AnonymizeRequest.keep` (plan 05). /scan carries it even though it
   * renders nothing, because `keep` does not act on the rendered string -
   * it removes the span from `spans`, `uncertainSpans` and `counts`, which
   * are exactly the fields this endpoint returns. Leaving it out would make
   * /scan report a DATE span that /anonymize, for the identical body, does
   * not - the same "two endpoints must not disagree about detection"
   * argument that put `structure: "auto"` in scan.ts.
   */
  keep: KeepList.optional(),
  /**
   * Same field, same semantics and same no-tier-gate as
   * `AnonymizeRequest.applyCustomRules` — carried here for the same reason
   * `keep` is: it acts on `spans`/`uncertainSpans`, which is exactly what
   * `/scan` returns, so leaving it off this schema would make `/scan`
   * report a custom-rule span that `/anonymize`, for the identical body,
   * does not. Not available on `/anonymize-file`.
   */
  applyCustomRules: z.boolean().optional(),
  /** Same field as `AnonymizeRequest.applyAllowlist`, carried for the same
   * reason as `applyCustomRules` just above. */
  applyAllowlist: z.boolean().optional(),
});
export type ScanRequest = z.infer<typeof ScanRequest>;

/**
 * Response of POST /api/v1/scan (Task T5, plan 03) — runs the identical
 * detection pipeline as /anonymize (same auth, entitlement, rate limits,
 * shared daily budget under the same `"anonymize"` bucket — a scan costs
 * the same as a full run, deliberately) but says WHAT is in a text without
 * ever handing back redacted output: there is no `anonymized` field here,
 * unlike `AnonymizeResponse`.
 *
 * `stats` is the identical `AnonymizeStats` shape (`counts` and
 * `uncertainCount` included, Task T2 plan 03), computed the same way and
 * gated by the same anon/free `REDACTED`-only category masking as
 * /anonymize — see that schema's own docstring.
 *
 * See `Receipt`'s own docstring for the general contract. Here,
 * `outputSha256` is the sha256 (lowercase hex, UTF-8) over `canonicalJson`
 * of `{ spans, uncertainSpans }` below — exactly the two fields this
 * endpoint hands back as its payload, using the already tier-masked PUBLIC
 * spans (never the raw ones, and never a rendered string — this endpoint
 * has none). See `packages/shared/src/canonical-json.ts`.
 */
export const ScanResponse = z.object({
  /** Auto-redacted spans. NOT reflected anywhere — /scan never renders
   * text — but present so a caller can see start/end/category/score. */
  spans: z.array(Span),
  /** Low-confidence model emissions, same meaning as
   * `AnonymizeResponse.uncertainSpans`. Always present (possibly empty). */
  uncertainSpans: z.array(Span),
  stats: AnonymizeStats,
  receipt: Receipt.nullable(),
});
export type ScanResponse = z.infer<typeof ScanResponse>;

/**
 * Honest degradation warning emitted by the docx reforge pipeline when the
 * source document held content the rebuild could not carry over verbatim
 * (footnotes, merged cells, textboxes, ...). `code` is one of the reforge
 * WarningCode values; `detail` is a short human string. Kept passthrough so
 * new warning codes surface without a schema bump on the web side.
 */
export const FileWarning = z
  .object({
    code: z.string(),
    detail: z.string(),
  })
  .passthrough();
export type FileWarning = z.infer<typeof FileWarning>;

/**
 * Response of POST /anonymize-file (docx in, redacted docx out). The rebuilt
 * document rides along base64-encoded in `file.contentBase64`; `spans` /
 * `uncertainSpans` drive the pre-download review surface, and `warnings`
 * reports what the rebuild could not carry verbatim. See
 * apps/api/src/routes/anonymize-file.ts.
 */
export const AnonymizeFileResponse = z.object({
  file: z.object({
    name: z.string(),
    mimeType: z.string(),
    contentBase64: z.string(),
  }),
  /** Auto-redacted spans, already replaced by markers in the rebuilt file. */
  spans: z.array(Span),
  /**
   * Low-confidence detections. Redact-more default (D8): these are ALSO
   * redacted in the returned file, but listed separately so the reviewer can
   * double-check them. Always present (possibly empty).
   */
  uncertainSpans: z.array(Span),
  warnings: z.array(FileWarning),
  stats: z.object({
    request_id: z.string(),
    extractedChars: z.number(),
    servedBy: z.string(),
    /** Same meaning and computation as `AnonymizeStats.counts` (Task T2, plan
     * 03) — distinct markers per category over `spans`, `REDACTED`-only for
     * anon/free. */
    counts: z.record(z.string(), z.number().int().nonnegative()),
    /** Same meaning as `AnonymizeStats.uncertainCount`. */
    uncertainCount: z.number().int().nonnegative(),
    /**
     * Structure handling that ran for this request (Plan 07) — see
     * `Structure`'s docstring. The file route always reports "text": a docx
     * or a PDF's extracted text is not "structure" in the JSON/CSV sense
     * this field tracks. Kept here (rather than a JSON-only extension) so
     * `AnonymizeFileTextResponse.stats` — which reuses this object via
     * `.shape.stats` — carries the same field without a second declaration.
     */
    structure: z.enum(["text", "json", "csv"]),
    /**
     * Same field, same semantics as `AnonymizeStats.kept` (plan 05, task T2):
     * categories the request asked to keep via the `keep` multipart field
     * and that were actually detected. Present only when at least one
     * category was kept; absent (never `[]`) otherwise, so every file
     * response recorded before this field existed still parses.
     */
    kept: z.array(KeepableCategory).optional(),
  }),
  /**
   * See `Receipt`'s own docstring. Here, `outputSha256` is the sha256 over
   * the returned document's BYTES — `file.contentBase64` decoded, never the
   * base64 text itself.
   */
  receipt: Receipt.nullable(),
});
export type AnonymizeFileResponse = z.infer<typeof AnonymizeFileResponse>;

/**
 * Response of POST /anonymize-file when the caller asked for plain text
 * instead of a rebuilt document (`outputFormat=text`, the default on the
 * API-key channel). Only reachable for PDF input: a PDF's layout is already
 * flattened to paragraphs by extraction, so text loses nothing a docx
 * rebuild would have kept. `stats` is structurally the same object as
 * `AnonymizeFileResponse.stats` — taken from it rather than retyped, so the
 * two response shapes cannot drift apart on that field.
 */
export const AnonymizeFileTextResponse = z.object({
  /** The anonymized document text: paragraphs separated by a blank line,
   * pages by a form feed (\f) — the same convention pdftotext uses. */
  text: z.string(),
  /** Auto-redacted spans, already replaced by markers in `text`. */
  spans: z.array(Span),
  /**
   * Low-confidence detections, same redact-more semantics as
   * `AnonymizeFileResponse.uncertainSpans`. Always present (possibly empty).
   */
  uncertainSpans: z.array(Span),
  warnings: z.array(FileWarning),
  stats: AnonymizeFileResponse.shape.stats,
  /**
   * See `Receipt`'s own docstring. Here, `outputSha256` is the sha256 over
   * the returned `text` string above, UTF-8 encoded.
   */
  receipt: Receipt.nullable(),
});
export type AnonymizeFileTextResponse = z.infer<typeof AnonymizeFileTextResponse>;

// --- v1.0 backend foundation schemas ---

export const Language = z.enum(["de", "en"]);
export type Language = z.infer<typeof Language>;

/**
 * What the user was trying to DO when they were sent into the auth chain.
 *
 * An enum, never a URL. The intent crosses the network twice on the way in
 * (the sign-in page hands it to the API, the API hands it back through the
 * magic-link email or the OAuth state) and it decides where the browser is
 * sent afterwards - which is the exact shape of an open-redirect hole if the
 * value is a destination. A closed set of names the API resolves to its own
 * paths cannot be one.
 *
 * The intent is single-use by construction: it rides the magic-link token or
 * the `oauth_states` row, both of which are consumed on redemption. It is
 * never persisted against the account, so it cannot resurface on a later
 * login.
 */
export const AuthIntent = z.enum(["trial"]);
export type AuthIntent = z.infer<typeof AuthIntent>;

export const Theme = z.enum(["light", "dark", "system"]);
export type Theme = z.infer<typeof Theme>;

/**
 * The `billing_subscriptions.status` enum from apps/api/src/db/schema.ts,
 * duplicated here as a literal type — same convention as
 * apps/api/src/billing/fungies/status.ts's `OurStatus`. Keep in sync with
 * schema.ts by hand; there is no generated single source of truth for a
 * Drizzle text-enum column's TS type across the api/shared package boundary.
 */
export const SubscriptionStatus = z.enum([
  "active",
  "trialing",
  "paused",
  "past_due",
  "canceled",
  "scheduled_cancel",
  "expired",
  "incomplete",
  "unpaid",
]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatus>;

/**
 * Subset of `SubscriptionStatus` that entitles a user to their plan's tier —
 * the canonical source `apps/api/src/billing/active-subscription.ts`'s
 * `ACTIVE_ISH_STATUSES` imports directly (so that export IS this array, not
 * a second hand-copy), and that `apps/web/src/pages/admin.tsx`'s admin
 * account-detail view imports too, for the same reason.
 *
 * Found by PR #273 review, twice independently: the admin detail view had
 * grown its OWN copy of this list, missing `past_due`, so an account with
 * exactly one `past_due` row — which the server still grants a tier for —
 * read as "no currently active subscription" to the operator. Exactly the
 * tier-as-proof-of-payment confusion AGENTS.md §6 already blamed for two
 * defects. A third hand-copy is not the fix for a duplication problem.
 *
 * `apps/api/src/billing/tier-recompute.ts`'s `QUALIFYING_STATUSES` mirrors
 * this same set independently (a `Set`, not imported from here) and is
 * pinned against `ACTIVE_ISH_STATUSES` by
 * `apps/api/src/billing/active-subscription.test.ts` — that pairing predates
 * this constant and is out of scope here; not touched.
 */
export const ACTIVE_ISH_SUBSCRIPTION_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "scheduled_cancel",
] as const;

/**
 * Subset of `SubscriptionStatus` a subscription cannot come back from. Nothing
 * can be granted for one of these, and no later event can change that.
 *
 * Deliberately only two. `incomplete` is the documented window before a
 * payment clears and routinely becomes `active`; `paused` resumes; `unpaid` is
 * a dunning state whose Fungies semantics we have never measured. Guessing any
 * of those into this list would turn a recoverable subscription into one we
 * stop reasoning about - the expensive direction of a wrong answer here.
 *
 * Used by `webhook-processor.ts` to tell a hopeless delivery from a merely
 * early one: an event with no resolvable user is retried while the
 * subscription is live (the missing `created` may still land), and ignored
 * once it is terminal (there is nothing left to grant either way).
 */
export const TERMINAL_SUBSCRIPTION_STATUSES = ["canceled", "expired"] as const;

/**
 * Public view of the user's active-ish billing subscription, if any. Never
 * carries a raw provider offer id.
 *
 * It used to carry a `pendingTier`/`pendingInterval`/`pendingEffectiveAt`
 * trio describing a scheduled downgrade. Downgrades are no longer offered
 * (owner decision 2026-09-03) and every accepted plan change now applies
 * synchronously, so there is no future state left to announce - the fields
 * went with the columns behind them.
 *
 * `status` and `cancelAt` mirror the DB columns directly. The account UI uses
 * `status === "scheduled_cancel"` to show a cancellation notice and hide the
 * plan-change / cancel entry points, since POST /billing/plan rejects both
 * while a cancellation is scheduled.
 */
export const UserSubscriptionPublic = z.object({
  interval: z.enum(["monthly", "annual"]),
  status: SubscriptionStatus,
  cancelAt: z.string().nullable(),
  /**
   * The date an ordinary cancellation declared right now would take effect,
   * or `null` when the server cannot name one (billing off, or a row without
   * a period end) and the declaration would go to manual review.
   *
   * Computed server-side by `ordinaryCancelEffectDate` — the same function
   * `POST /cancellation` uses to decide what it stores — so a surface that
   * shows this value cannot promise a date the cancellation route would not
   * honour. Clients render it or fall back to the abstract wording; they must
   * never derive a date of their own from the other fields.
   */
  ordinaryCancelEffectDate: z.string().nullable(),
});
export type UserSubscriptionPublic = z.infer<typeof UserSubscriptionPublic>;

/**
 * The running trial, or null. Only ever the CURRENT one: an expired trial is
 * reported as null rather than as a row with a date in the past, because
 * every consumer of this field asks "may they use it", never "did they once".
 */
export const UserTrialPublic = z.object({
  tier: z.enum(["docs", "api", "pro"]),
  endsAt: z.string(),
});
export type UserTrialPublic = z.infer<typeof UserTrialPublic>;

export const UserPublic = z.object({
  id: z.string(),
  email: z.string().email(),
  emailVerifiedAt: z.string().nullable(),
  createdAt: z.string(),
  language: Language,
  theme: Theme,
  markerFormat: MarkerFormat,
  customMarkerMap: z.record(z.string(), z.string()).nullable(),
  tier: z.enum(["free", "docs", "api", "pro", "ultra"]),
  /**
   * Operator-only flag. Frontend uses this to gate the /admin route.
   * Bypasses rate-limit + char-limit on the API side. NOT user-settable.
   */
  isAdmin: z.boolean(),
  /**
   * The user's newest active-ish billing subscription, or `null` when none
   * exists (free user, or a paid tier set by an admin without a provider
   * subscription behind it). Always present — the server never omits the
   * key — so the client can distinguish "no subscription" from "field not
   * loaded yet".
   */
  subscription: UserSubscriptionPublic.nullable(),
  /**
   * The running trial, or `null` when there is none or it has ended. Always
   * present, like `subscription`, so a client can tell "no trial" from "not
   * loaded yet".
   */
  trial: UserTrialPublic.nullable(),
  /**
   * Whether this account could start a trial right now. Computed server-side
   * by the same function `POST /trial/start` refuses on, so a surface that
   * shows the button cannot offer one the route would reject.
   */
  trialAvailable: z.boolean(),
  /**
   * Whether this account has dismissed the "Falsch markiert?" feedback
   * explainer (BACKLOG.md, "Der Feedback-Knopf erklärt nicht, was er tut").
   * `false` (the default for every account) means the explainer is still
   * due the next time the button is clicked. Independent of
   * `feedbackDisabled` below - a user can keep the button but skip the
   * one-time explanation.
   */
  feedbackModalDismissed: z.boolean(),
  /**
   * Whether this account has opted out of the "Falsch markiert?" feedback
   * button entirely. When `true`, `ConfidenceMarker` never renders the
   * button (see `RenderArgs.feedbackDisabled` in
   * apps/web/src/components/redact/span-renderer.tsx) - a stronger opt-out
   * than `feedbackModalDismissed`, set from the explainer modal's second
   * checkbox or from Account settings.
   */
  feedbackDisabled: z.boolean(),
});
export type UserPublic = z.infer<typeof UserPublic>;

export const SessionPublic = z.object({
  id: z.string(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  expiresAt: z.string(),
  userAgent: z.string().nullable(),
});
export type SessionPublic = z.infer<typeof SessionPublic>;

export const MagicLinkRequest = z.object({
  email: z.string().email().max(320),
  turnstile_token: z.string().min(1).max(2048),
  // Honeypot: must be empty
  website: z.string().max(0).optional(),
  // Current UI locale of the requester. Carried through the redeem link so
  // the post-login redirect lands on the right locale instead of guessing.
  // Optional for back-compat with older clients; the API falls back to its
  // own default when omitted.
  lang: Language.optional(),
  // What the click that started this was for. Carried through the redeem
  // link so the post-login redirect can land on the surface that offers it
  // instead of the home page. Absent means "just sign me in".
  //
  // `.catch` on the FIELD, not the object: without it an unrecognized value
  // fails the whole body, and this route answers a failed parse with the
  // same silent 202 it gives a honeypot hit - no email, no error, a sign-in
  // attempt lost to a routing hint. Nothing is bought by strictness here;
  // the redirect is safe because `postLoginRedirect` only ever resolves
  // known members, not because the parse rejected the unknown ones.
  intent: AuthIntent.optional().catch(undefined),
});
export type MagicLinkRequest = z.infer<typeof MagicLinkRequest>;

export const UserUpdate = z.object({
  language: Language.optional(),
  theme: Theme.optional(),
  markerFormat: MarkerFormat.optional(),
  customMarkerMap: z.record(z.string(), z.string()).nullable().optional(),
  feedbackModalDismissed: z.boolean().optional(),
  feedbackDisabled: z.boolean().optional(),
});
export type UserUpdate = z.infer<typeof UserUpdate>;

export const DeleteAccountRequest = z.object({
  /**
   * The account's own email address, echoed back. Checked case-insensitively
   * server-side against the session's user.
   *
   * It used to be typed by hand in the delete dialog; since the dialog moved
   * to a three-second hold gesture the web client fills it from the session,
   * so for that caller this field is no longer a second factor - the gesture
   * is. It still stands between a direct API call and an account, which is
   * why it stays required rather than being dropped.
   */
  confirm: z.string().min(1),
});
export type DeleteAccountRequest = z.infer<typeof DeleteAccountRequest>;

export const ApiKeyCreateRequest = z.object({
  name: z.string().min(1).max(60),
});
export type ApiKeyCreateRequest = z.infer<typeof ApiKeyCreateRequest>;

export const ApiKeyPublic = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type ApiKeyPublic = z.infer<typeof ApiKeyPublic>;

// On create only — the plaintext key is shown ONCE
export const ApiKeyCreateResponse = ApiKeyPublic.extend({
  key: z.string(),
});
export type ApiKeyCreateResponse = z.infer<typeof ApiKeyCreateResponse>;

// ---- Custom Redaction Rules ----

/**
 * Same 8-value list as model-categories.ts's DISPLAY_CATEGORIES — derived
 * from it rather than redeclared, so the two can't silently drift apart.
 * `z.enum` needs a tuple literal (not just `readonly string[]`); DISPLAY_
 * CATEGORIES is already declared `as const`, so its tuple-literal type
 * carries through here unchanged.
 */
export const RULE_CATEGORIES = DISPLAY_CATEGORIES;
export type RuleCategory = (typeof RULE_CATEGORIES)[number];

export const CustomRedactionRule = z.object({
  id: z.string(),
  name: z.string().min(1).max(80),
  pattern: z.string().min(1).max(300),
  category: z.enum(RULE_CATEGORIES),
  enabled: z.boolean(),
  description: z.string().max(200).optional(),
  createdAt: z.string(),
});
export type CustomRedactionRule = z.infer<typeof CustomRedactionRule>;

export const CustomRedactionRuleCreate = CustomRedactionRule.omit({
  id: true,
  createdAt: true,
}).extend({
  // Not trimmed, unlike AllowlistEntryCreate.value: whitespace inside a
  // regex is part of what it matches.
  pattern: z.string().min(PATTERN_LIST_MIN_LENGTH).max(300),
});
export type CustomRedactionRuleCreate = z.infer<typeof CustomRedactionRuleCreate>;

export const CustomRedactionRuleUpdate = CustomRedactionRuleCreate.partial();
export type CustomRedactionRuleUpdate = z.infer<typeof CustomRedactionRuleUpdate>;

// ---- Allowlist Entries ----

export const AllowlistEntry = z.object({
  id: z.string(),
  value: z.string().min(1).max(200),
  caseSensitive: z.boolean(),
  enabled: z.boolean(),
  createdAt: z.string(),
});
export type AllowlistEntry = z.infer<typeof AllowlistEntry>;

export const AllowlistEntryCreate = z.object({
  // Trimmed before the length check: "  a " is one character of intent, and
  // the matcher ignores surrounding whitespace anyway (shared/allowlist.ts).
  value: z.string().trim().min(PATTERN_LIST_MIN_LENGTH).max(200),
  caseSensitive: z.boolean().default(true),
  enabled: z.boolean().default(true),
});
export type AllowlistEntryCreate = z.infer<typeof AllowlistEntryCreate>;

export const AllowlistEntryUpdate = AllowlistEntryCreate.partial();
export type AllowlistEntryUpdate = z.infer<typeof AllowlistEntryUpdate>;

// ---- Limits ----

export const LimitsResponse = z.object({
  /**
   * Limit that applies to THIS specific request (resolved from auth context).
   * `tier` is "anon" for unauthenticated requests.
   */
  current: z.object({
    tier: z.enum(["anon", "free", "docs", "api", "pro", "ultra"]),
    channel: z.enum(["web", "api"]),
    daily_limit: z.number().int(),
    /**
     * Whether the caller is actually entitled to call `/anonymize`. Always true
     * on the web channel. False for free-tier API keys (the key survives a
     * downgrade but every call bounces with 402 `account_tier_required` until
     * the account re-upgrades). True for admin API keys regardless of tier.
     */
    entitled: z.boolean(),
  }),
  /**
   * Full reference table for the public docs page — derived from env vars.
   * Lets the docs page render the complete tier×channel grid without coupling
   * to specific environment values.
   */
  reference: z.object({
    web: z.object({
      anon: z.number().int(),
      free: z.number().int(),
      docs: z.number().int(),
      api: z.number().int(),
      pro: z.number().int(),
      ultra: z.number().int(),
    }),
    api: z.object({
      free: z.number().int(),
      docs: z.number().int(),
      api: z.number().int(),
      pro: z.number().int(),
      ultra: z.number().int(),
    }),
  }),
  /** Burst / auth-endpoint limits */
  burst: z.object({
    magic_link_per_hour: z.number().int(),
  }),
  anonymize: z.object({
    /**
     * @deprecated Use `reference.web.anon` instead.
     */
    anonymous_per_day: z.number().int(),
    /**
     * @deprecated Use `reference.web.free` instead.
     */
    user_per_day: z.number().int(),
    /**
     * @deprecated Use `reference.api.free` instead.
     */
    apikey_per_day: z.number().int(),
  }),
  auth: z.object({
    /**
     * @deprecated Use `burst.magic_link_per_hour` instead.
     */
    magic_link_per_hour: z.number().int(),
  }),
});
export type LimitsResponse = z.infer<typeof LimitsResponse>;

// ---- Billing (Fungies) ----
//
// Checkout is entirely client-side: the api's only job is resolving which
// Fungies checkout element + offer a (plan, interval) pair maps to and
// handing back enough for the browser to open it — there is no server-issued
// redirect URL. The browser then calls `Fungies.Checkout.open()`
// (@fungies/fungies-js, see apps/web/src/lib/fungies.ts) with `checkoutUrl`
// and `offerId` from the response below. `checkoutUrl` identifies the
// checkout ELEMENT — a Fungies dashboard object carrying exactly one
// subscription offer, so there is one element (and one URL) per
// tier/interval, six total. `customFields.checkout_ref` carries a
// server-minted single-use nonce through Fungies onto the resulting webhook
// events; the webhook processor resolves it against its own
// billing_checkout_intents row to find the account (see
// apps/api/src/billing/fungies/normalize.ts). The nonce means nothing on its
// own — it crosses the buyer's browser in an editable query parameter — and
// the key must be declared on the Fungies product or the value is dropped
// silently (measured 2026-08-20).

/**
 * Request body for POST /api/v1/billing/checkout. `plan` maps to a paid tier
 * (docs/api/pro — free has no product, ultra doesn't sell yet); `interval`
 * selects the monthly or annual price.
 */
export const BillingCheckoutRequest = z.object({
  plan: z.enum(["docs", "api", "pro"]),
  interval: z.enum(["monthly", "annual"]),
});
export type BillingCheckoutRequest = z.infer<typeof BillingCheckoutRequest>;

/**
 * Response of POST /api/v1/billing/checkout — everything the browser needs
 * to open the Fungies overlay directly (see the section comment above).
 * `checkoutUrl` is the checkout-element URL; `offerId` goes into `items[]`
 * alongside it, since a checkout element only accepts the offer(s) it was
 * configured with in the dashboard. `customFields` is `{ checkout_ref }`: an
 * opaque, single-use nonce the server minted for this checkout, never an
 * identifier. It is a record rather than a fixed shape because the client
 * passes it through to Fungies verbatim instead of reading anything out of
 * it — and it must stay that way, since the value round-trips through a
 * query parameter the buyer can edit.
 */
export const BillingCheckoutResponse = z.object({
  checkoutUrl: z.string(),
  offerId: z.string(),
  customerEmail: z.string(),
  customFields: z.record(z.string()),
});
export type BillingCheckoutResponse = z.infer<typeof BillingCheckoutResponse>;

export const BillingPortalResponse = z.object({ portalUrl: z.string() });
export type BillingPortalResponse = z.infer<typeof BillingPortalResponse>;

/**
 * Request body shared by POST /api/v1/billing/plan/preview and
 * POST /api/v1/billing/plan (self-serve plan change). Same shape as
 * BillingCheckoutRequest — a distinct type because the two flows are not
 * interchangeable (checkout has no existing subscription; plan-change requires one).
 */
export const BillingPlanChangeRequest = z.object({
  plan: z.enum(["docs", "api", "pro"]),
  interval: z.enum(["monthly", "annual"]),
});
export type BillingPlanChangeRequest = z.infer<typeof BillingPlanChangeRequest>;

/**
 * Response of POST /api/v1/billing/plan/preview. Money fields, when present,
 * are strings in the price's smallest currency unit — never floats, to avoid
 * precision loss.
 *
 * Accepted regression (Fungies migration, see the migration plan's "Accepted
 * regressions" section): Fungies has no equivalent of Paddle's
 * `previewUpdateSubscription`, so the provider is never called here. Every
 * money field is therefore always `null` — nothing computes or estimates a
 * proration figure client-side; the UI must say "the prorated difference is
 * charged now" rather than naming an amount. `nextBilledAt` is always the
 * current subscription's `currentPeriodEnd`.
 *
 * A 200 from this endpoint means the change is possible AND immediate; the
 * refusals (downgrade, interval change) come back as 400s with their own
 * code, which is why there is no longer a flag distinguishing the two.
 */
export const BillingPlanPreviewResponse = z.object({
  immediateCharge: z.string().nullable(),
  immediateCredit: z.string().nullable(),
  nextBilledAt: z.string().nullable(),
  nextBilledAmount: z.string().nullable(),
  currencyCode: z.string().nullable(),
});
export type BillingPlanPreviewResponse = z.infer<typeof BillingPlanPreviewResponse>;

/**
 * Response of POST /api/v1/billing/plan. A 200 means the change is already
 * live: the only changes the route still accepts (a tier upgrade, or the
 * lateral docs<->api swap) reach the provider synchronously. There is no
 * deferred variant left to report a date for - downgrades are not offered and
 * an interval change is refused, both with their own error code.
 */
export const BillingPlanChangeResponse = z.object({
  ok: z.literal(true),
});
export type BillingPlanChangeResponse = z.infer<typeof BillingPlanChangeResponse>;

// ---- Cancellation (§ 312k BGB "Kündigungsbutton") ----

/**
 * "Ordentliche" (ordinary/regular, notice-period) vs "außerordentliche"
 * (extraordinary/for-cause) termination. Only `ordinary` is ever eligible for
 * the auto-execute path in POST /cancellation — an extraordinary termination
 * always needs a human to read the stated reason before anything is applied
 * provider-side.
 */
export const CancellationType = z.enum(["ordinary", "extraordinary"]);
export type CancellationType = z.infer<typeof CancellationType>;

const CALENDAR_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * True calendar validity, not just ISO shape. A plain regex (or zod's own
 * `.date()`, which is regex-only under the hood) accepts "2026-13-45" as a
 * syntactically shaped string, and `new Date(...)` on it returns NaN — but
 * on a syntactically in-range, calendar-nonexistent date like "2026-02-30"
 * or "2026-04-31", `new Date(...)` does NOT return NaN: it silently rolls
 * the date over to March 2 / May 1. Both classes must be rejected here —
 * this field ends up in a legal cancellation record and its confirmation
 * email/download; a corrupted-not-rejected date would misstate what the
 * user actually declared. Round-tripping through Date.UTC and comparing the
 * normalized fields back against the parsed input catches both.
 */
function isValidCalendarDateString(value: string): boolean {
  const match = CALENDAR_DATE_RE.exec(value);
  if (!match || !match[1] || !match[2] || !match[3]) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

/**
 * Request body for POST /api/v1/cancellation. No auth, no CAPTCHA — § 312k
 * BGB requires the cancellation declaration to be reachable without any
 * hurdle, so this endpoint deliberately carries none of the anti-abuse
 * friction other public routes use (see cancellation.ts's rate limit for the
 * one exception the law does tolerate: a generous per-IP throttle).
 *
 * `reason` is required (min length enforced by superRefine, not here) when
 * `type` is "extraordinary" and optional otherwise. `requestedEffectDate`
 * is a plain `YYYY-MM-DD` date; omitted means "as soon as legally possible"
 * (the ordinary notice period, or immediately if none applies).
 */
export const CancellationRequest = z
  .object({
    type: CancellationType.default("ordinary"),
    reason: z.string().trim().max(2000).optional(),
    email: z.string().email().max(320),
    name: z.string().trim().max(200).optional(),
    requestedEffectDate: z
      .string()
      .refine(isValidCalendarDateString, {
        message: "requestedEffectDate must be a valid calendar date (YYYY-MM-DD)",
      })
      .optional(),
    // UI locale of the requester, for the confirmation email's language.
    // Optional for API-only callers; the server falls back to its own
    // default when omitted (same pattern as MagicLinkRequest.lang above).
    lang: Language.optional(),
  })
  .superRefine((val, ctx) => {
    if (val.type === "extraordinary" && (!val.reason || val.reason.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: "reason is required for an extraordinary termination",
      });
    }
  });
export type CancellationRequest = z.infer<typeof CancellationRequest>;

/**
 * Response of POST /api/v1/cancellation. Always 200 on a structurally valid
 * request — see cancellation.ts's docstring for why "no matching account"
 * is not an error. Echoes the full declaration back (the confirmation view
 * displays and downloads exactly this) plus the two server-decided facts:
 * `receivedAt` (Zugang — the legally relevant receipt timestamp) and
 * `effectDate` (null when not yet determinable; the client then shows the
 * "we'll confirm the exact date separately" copy the law requires).
 */
export const CancellationResponse = z.object({
  id: z.string(),
  receivedAt: z.string(),
  type: CancellationType,
  reason: z.string().nullable(),
  email: z.string(),
  name: z.string().nullable(),
  requestedEffectDate: z.string().nullable(),
  effectDate: z.string().nullable(),
  autoExecuted: z.boolean(),
});
export type CancellationResponse = z.infer<typeof CancellationResponse>;

// ---- Admin Portal (Phase 6) ----

/**
 * Row shape for the operator user-list table. Deliberately narrow — no
 * secrets, no API key material, no IP hashes. Returned by GET /admin/users.
 */
export const AdminUserListItem = z.object({
  id: z.string(),
  email: z.string(),
  tier: z.enum(["free", "docs", "api", "pro", "ultra"]),
  isAdmin: z.boolean(),
  createdAt: z.string(),
  tierUpdatedAt: z.string(),
});
export type AdminUserListItem = z.infer<typeof AdminUserListItem>;

/**
 * Response of GET /admin/users. `total` counts rows matching the CURRENT `q`
 * filter — what the pagination controls need to know whether more accounts
 * exist beyond this window. Same items/total/limit/offset shape as
 * AdminCancellationQueueResponse, for the same reason: this endpoint used to
 * return a bare `AdminUserListItem[]` with no total, so the UI had no way to
 * tell an operator the list silently truncated at whatever `limit` defaulted
 * to (fixed alongside adding pagination to the Users tab).
 */
export const AdminUserListResponse = z.object({
  items: z.array(AdminUserListItem),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type AdminUserListResponse = z.infer<typeof AdminUserListResponse>;

/**
 * Audit-log event surface for the admin portal. `metadata` is the parsed
 * JSON object (already decoded server-side), not the raw text column.
 */
export const AdminAuditEvent = z.object({
  id: z.string(),
  event: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string(),
});
export type AdminAuditEvent = z.infer<typeof AdminAuditEvent>;

/**
 * One row from `billing_subscriptions` for the admin account-detail view.
 * ALL rows for the account, not just the active-ish one — an operator
 * debugging "I paid and got nothing" needs exactly the rows the customer
 * account UI hides: `incomplete`, `canceled`, `unpaid`. Field list mirrors
 * the export route's billing selection (users.me-export.ts) minus nothing —
 * these ids are already the account owner's own data in that export, so
 * there is no narrower cut for the operator view.
 */
export const AdminSubscriptionItem = z.object({
  id: z.string(),
  provider: z.enum(["creem", "fungies", "stripe", "paddle"]),
  externalId: z.string(),
  externalCustomerId: z.string().nullable(),
  status: SubscriptionStatus,
  planId: z.string(),
  tier: z.enum(["docs", "api", "pro"]),
  interval: z.enum(["monthly", "annual"]),
  currentPeriodEnd: z.string().nullable(),
  cancelAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminSubscriptionItem = z.infer<typeof AdminSubscriptionItem>;

/**
 * The account's `trials` row for the admin detail view, or the endpoint
 * omits the field (null). Deliberately narrower than the full `trials` row:
 * `emailNormalized` is excluded — it is the plaintext address the
 * eligibility check runs on, and the detail view has no use for it (Charter
 * §1: least data an operator needs to act).
 */
export const AdminTrialDetail = z.object({
  id: z.string(),
  tier: z.enum(["docs", "api", "pro"]),
  startedAt: z.string(),
  endsAt: z.string(),
});
export type AdminTrialDetail = z.infer<typeof AdminTrialDetail>;

/**
 * One `cancellation_declarations` row for the admin detail view. Unlike
 * audit_log this table legitimately carries content (email, name, reason) —
 * it is the § 312k BGB declaration itself, and the extraordinary-termination
 * path is worked by hand today with no surface at all. `matchedUserId` is
 * omitted: the caller already filtered on it (it's the account this view is
 * for), so echoing it back would be redundant, not additive.
 */
export const AdminCancellationDeclarationItem = z.object({
  id: z.string(),
  createdAt: z.string(),
  type: CancellationType,
  reason: z.string().nullable(),
  email: z.string(),
  name: z.string().nullable(),
  requestedEffectDate: z.string().nullable(),
  effectDate: z.string().nullable(),
  autoExecuted: z.boolean(),
  subscriptionId: z.string().nullable(),
});
export type AdminCancellationDeclarationItem = z.infer<typeof AdminCancellationDeclarationItem>;

/**
 * Counts-only usage summary for the admin detail view — Charter §1: never
 * request content, never detected spans. `totalChars` sums
 * `usage_events.chars`, which is only ever a character count (populated for
 * budget-checked calls, 0 elsewhere) — never text.
 */
export const AdminUsageSummary = z.object({
  windowDays: z.number().int().positive(),
  eventCount: z.number().int().nonnegative(),
  totalChars: z.number().int().nonnegative(),
});
export type AdminUsageSummary = z.infer<typeof AdminUsageSummary>;

/**
 * Response of GET /admin/users/:id — the full account-detail bundle for the
 * admin portal's per-account view. `user` reuses `AdminUserListItem` rather
 * than a parallel type (same five fields the list row already shows).
 * `subscriptions` is ALL rows (see AdminSubscriptionItem), `trial` and
 * `usage` are always present (never-loaded is not a state this endpoint has —
 * it returns the full bundle or nothing at all, on a 404).
 */
export const AdminAccountDetail = z.object({
  user: AdminUserListItem,
  subscriptions: z.array(AdminSubscriptionItem),
  trial: AdminTrialDetail.nullable(),
  cancellationDeclarations: z.array(AdminCancellationDeclarationItem),
  usage: AdminUsageSummary,
});
export type AdminAccountDetail = z.infer<typeof AdminAccountDetail>;

/**
 * Best-effort classification of why a cancellation declaration is (or was)
 * sitting in manual review, for GET /admin/cancellations. `null` for a row
 * that WAS auto-executed — "why is this open" doesn't apply to it.
 *
 * This is deliberately coarser than `CancellationManualReason`
 * (apps/api/src/lib/email.ts): that finer enum is computed at request time
 * inside cancellation.ts but never persisted — it only ever reaches an
 * operator notification email or a log line. So a row read back later can
 * only prove what its stored columns prove:
 *   - "no_account_match": matchedUserId is null — no account exists for the
 *     submitted email.
 *   - "extraordinary": a for-cause termination — always manual by design,
 *     independent of account/subscription state.
 *   - "needs_review": matched, ordinary, but subscriptionId is still null.
 *     Per that column's own comment in schema.ts, that covers four distinct
 *     causes (no active subscription, billing disabled, a requested date
 *     past the current billing period, or a provider call that failed) which
 *     cannot be told apart from the row alone without re-deriving live
 *     billing state — which would answer "would this succeed NOW", not "why
 *     did it fail THEN". Folded into one bucket rather than guessed at.
 */
export const AdminCancellationReasonHint = z.enum([
  "no_account_match",
  "extraordinary",
  "needs_review",
]);
export type AdminCancellationReasonHint = z.infer<typeof AdminCancellationReasonHint>;

/**
 * One row for the operator cancellation queue (GET /admin/cancellations),
 * spanning ALL accounts — unlike `AdminCancellationDeclarationItem`, which is
 * already scoped to one account by the caller's :id and so omits
 * `matchedUserId` as redundant. Here it's the opposite: which account (if
 * any) this declaration ties to is exactly the fact the operator needs, to
 * jump into that account's detail view.
 */
export const AdminCancellationQueueItem = AdminCancellationDeclarationItem.extend({
  matchedUserId: z.string().nullable(),
  reasonHint: AdminCancellationReasonHint.nullable(),
  // Set once a human closes the case by hand (PATCH
  // /admin/cancellations/:id/resolve), independent of autoExecuted — see
  // that column's comment in apps/api/src/db/schema.ts. Both null together
  // means still open; resolvedAt implies resolvedBy (the route always sets
  // both in the same write) except for rows resolved before an admin's
  // account was later deleted, where resolvedBy nulls out on cascade.
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
});
export type AdminCancellationQueueItem = z.infer<typeof AdminCancellationQueueItem>;

/**
 * Response of GET /admin/cancellations. `total` counts rows matching the
 * CURRENT `filter` — what the pagination controls need to know whether more
 * rows exist beyond this window. `GET /admin/users` repeats the same
 * items/total/limit/offset shape for the same reason (see
 * AdminUserListResponse) — it used to return a bare array with no total,
 * which is exactly the mistake this shape exists to avoid.
 * `openCount` is independent of `filter` — always the count of unresolved
 * (autoExecuted = false) declarations, because that is the backlog number an
 * operator wants at a glance regardless of which view they're on.
 */
export const AdminCancellationQueueResponse = z.object({
  items: z.array(AdminCancellationQueueItem),
  total: z.number().int().nonnegative(),
  openCount: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type AdminCancellationQueueResponse = z.infer<typeof AdminCancellationQueueResponse>;

/**
 * One row from `billing_provider_orphans` — a provider subscription
 * currently considered active-ish by Fungies that has no local
 * `billing_subscriptions` row at all ("paid, never provisioned"). Returned
 * by GET /admin/orphan-subscriptions, the persisted counterpart of
 * `reconcile-subscriptions.ts`'s `billing_provider_orphan_subscriptions` gauge.
 *
 * Deliberately narrow — no `userId` field exists, because there is no local
 * user for this row by definition. `firstSeenAt` is the field this view
 * exists to surface prominently: how long the money has been unaccounted
 * for. `externalId` is a re-identifiable provider id (AGENTS.md §6) —
 * present here because an operator needs it to actually look the
 * subscription up at the provider, same tradeoff the underlying table makes.
 */
export const AdminOrphanSubscriptionItem = z.object({
  id: z.string(),
  externalId: z.string(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
});
export type AdminOrphanSubscriptionItem = z.infer<typeof AdminOrphanSubscriptionItem>;

/**
 * Response of GET /admin/orphan-subscriptions. `total` counts the whole
 * `billing_provider_orphans` table — there is no filter on this endpoint, so
 * unlike AdminUserListResponse's `q` or AdminCancellationQueueResponse's
 * `filter` there is no WHERE clause the count needs to mirror. Same
 * items/total/limit/offset shape as those two, for the same reason: this
 * endpoint used to return a bare `AdminOrphanSubscriptionItem[]` with no
 * total, so the operator had no way to tell whether the view — the one that
 * exists specifically to show "someone paid and got nothing" — silently
 * truncated at the default limit.
 */
export const AdminOrphanSubscriptionsResponse = z.object({
  items: z.array(AdminOrphanSubscriptionItem),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type AdminOrphanSubscriptionsResponse = z.infer<typeof AdminOrphanSubscriptionsResponse>;

/**
 * One row from `billing_webhook_events` for GET /admin/webhook-events — the
 * idempotency/audit ledger for inbound Fungies webhooks. The table itself
 * (apps/api/src/db/schema.ts) never stores the raw request body or payload,
 * only these five columns, so there is nothing to redact from the SELECT
 * beyond a decision on `id` itself: it is the provider's own event id
 * ("evt_..."), which is a re-identifiable provider identifier (AGENTS.md
 * §6) — present here for the same reason `AdminOrphanSubscriptionItem`
 * carries `externalId`: an operator needs it to correlate against the
 * provider's own event history (Fungies dashboard) when answering "did
 * event X actually arrive", which is the entire reason this view exists,
 * AND — the half of the argument that actually satisfies Charter §6's "never
 * with `forever` retention" — the row this id lives on is bounded to the
 * same 90-day window as `billing_provider_orphans`
 * (`apps/api/src/cron/cleanup.ts`'s `WEBHOOK_EVENT_RETENTION_DAYS`), not kept
 * indefinitely. `outcome` is null only in the brief window between the
 * row's insert and the processing call resolving (see the column's own
 * comment in schema.ts) — the API surfaces that as the literal string
 * `"pending"` rather than `null`, so a row stuck there for any length of
 * time is something an operator would notice and question rather than
 * something a client-side nullable check silently absorbs.
 */
export const AdminWebhookEventItem = z.object({
  id: z.string(),
  eventType: z.string(),
  receivedAt: z.string(),
  processedAt: z.string().nullable(),
  outcome: z.enum(["processed", "ignored", "error", "pending"]),
});
export type AdminWebhookEventItem = z.infer<typeof AdminWebhookEventItem>;

/**
 * Response of GET /admin/webhook-events. Same items/total/limit/offset
 * envelope as every other admin list (see AdminOrphanSubscriptionsResponse's
 * docstring for why: a bare array gives the operator no way to tell whether
 * the list silently truncated at the default limit) — `total` counts rows
 * matching the CURRENT filters (eventType/outcome/id/from/to), mirroring
 * AdminCancellationQueueResponse's `filter`-scoped total rather than
 * AdminOrphanSubscriptionsResponse's unfiltered one, because this endpoint
 * does take filters.
 */
export const AdminWebhookEventsResponse = z.object({
  items: z.array(AdminWebhookEventItem),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type AdminWebhookEventsResponse = z.infer<typeof AdminWebhookEventsResponse>;

// ---- UX Telemetry (UNKLAR layer, D8) ----

/**
 * Names for POST /api/v1/events (apps/api/src/routes/events.ts). Fired when
 * a user resolves an ambiguous UNKLAR marker, interacts with the copy
 * guard, flags a redaction marker as wrongly detected (`span_feedback`,
 * Task T6 plan 03 — the "does this mechanism over-mark?" instrument; see
 * UxEventRequest below for its two extra bound fields), or marks something
 * we missed (`manual_redact`). Count-only instrument — see UxEventRequest
 * below for the privacy constraint this enum exists to enforce.
 *
 * `manual_redact` exists because every other event here measures
 * OVER-redaction, and the two directions are not symmetrically expensive:
 * over-redaction annoys, and Charter §4 calls it correct behaviour when in
 * doubt; under-redaction hands out a name. Until it was added we had an
 * instrument for the harmless direction and none for the dangerous one, and
 * no metric showed the difference.
 *
 * What it does NOT deliver, said out loud so nobody reads more into the
 * number than it carries: the `category` on this event comes from the
 * USER'S choice in the popover, never from a detection, so it says what the
 * person thought they were marking and nothing about what the model saw.
 * And a user who marks nothing stays indistinguishable from one who found
 * nothing to mark — this counts corrections, not misses.
 *
 * `mention_propagated` is the SECOND under-redaction signal and is kept
 * apart from the first on purpose. When a user resolves an UNKLAR span,
 * stage 2 of that decision scans the plain text for further occurrences of
 * the same surface that the pipeline marked NOWHERE
 * (`findExactMentionMatches`, guarded against everything already on the
 * page) and redacts them too. Those are real misses — but nobody looked at
 * them: our own code inferred them from one human decision. Folding them
 * into `manual_redact` would make that counter read as more human
 * observation than actually happened, so they get their own name. It is
 * this signal, not the other, that sees the documented weak spot — a name
 * repeated across quote levels in a forwarded thread or across a speaker's
 * chat lines.
 *
 * It fires ONCE per propagation, not once per span found, matching every
 * other event here (all are per-gesture). How MANY occurrences one
 * propagation caught is deliberately not carried: this payload admits no
 * free-form or unbounded field, and a count would be one.
 */
export const UxEvent = z.enum([
  "unklar_confirm", // user accepted the model's category
  "unklar_recategorize", // user picked a different category
  "unklar_keep", // user restored the text (not PII)
  "copy_guard_shown",
  "copy_guard_overridden",
  "span_feedback", // user flagged an already-redacted marker as wrong
  "manual_redact", // user marked something the pipeline missed
  "mention_propagated", // propagation found further unmarked occurrences
]);
export type UxEvent = z.infer<typeof UxEvent>;

/**
 * Request body for POST /api/v1/events. `category` is the bounded 8-value
 * RULE_CATEGORIES enum, reused as-is — never redeclare or widen this list.
 * `bucket` and `source` are `span_feedback`-only (Task T6, plan 03): the
 * confidence bucket the marker was shown under (`badgeStyle`'s bucket,
 * shared/../apps/web/src/components/redact/span-renderer.tsx) and which
 * detection mechanism produced the span (`SpanSource.shape.kind` above —
 * never `source.name`, which is user content, not a bounded value). Both
 * are optional because every other UxEvent never sets them, AND because
 * anon/free callers never see `source` at all (tier-honesty — Task T1
 * strips it before those tiers' responses), so a `span_feedback` fired from
 * one of those tiers legitimately carries no `source`.
 * Nothing free-form may ever be added to this payload: every field here
 * becomes a Prometheus label (`uxEventsTotal` for every event except
 * `span_feedback`, which gets its OWN counter — `spanFeedbackTotal` — in
 * apps/api/src/metrics.ts; see that file for why), so a free-form field
 * would be both an unbounded-cardinality bug and a privacy regression
 * (AGENTS.md §1 — counts only, never content). No span text, no
 * per-request category tied to specific content, no user identifier beyond
 * the tier bucket the server derives itself.
 */
export const UxEventRequest = z.object({
  event: UxEvent,
  category: z.enum(RULE_CATEGORIES).optional(),
  bucket: z.enum(["high", "medium", "low"]).optional(),
  source: SpanSource.shape.kind.optional(),
});
export type UxEventRequest = z.infer<typeof UxEventRequest>;

// ---- Rule Packs (import + starter packages, plan 09) ----

/**
 * Repo-shipped starter rule packages (packages/shared/src/rule-packs/*.json)
 * carry this format tag. Bump the version suffix on any breaking change to
 * the pack shape below — never reuse "1" for an incompatible shape.
 */
export const RULE_PACK_FORMAT = "unpii-rulepack/1";

export const LocalizedText = z.object({
  de: z.string().min(1).max(200),
  en: z.string().min(1).max(200),
});
export type LocalizedText = z.infer<typeof LocalizedText>;

// Reused as-is, not redeclared — a pack rule/allowlist entry is exactly the
// same shape a user creates by hand through the existing CRUD routes.
export const RulePackRule = CustomRedactionRuleCreate;
export type RulePackRule = z.infer<typeof RulePackRule>;

export const RulePackAllowlistEntry = AllowlistEntryCreate;
export type RulePackAllowlistEntry = z.infer<typeof RulePackAllowlistEntry>;

/**
 * A repo-shipped starter package. Unlike the import envelopes below, id/name/
 * description are REQUIRED here — a package without an id is not installable
 * server-side via `{ pack: "<id>" }`.
 */
export const RulePack = z.object({
  format: z.literal(RULE_PACK_FORMAT),
  id: z.string().min(1).max(40),
  name: LocalizedText,
  description: LocalizedText,
  rules: z.array(RulePackRule).max(200),
  allowlist: z.array(RulePackAllowlistEntry).max(200),
});
export type RulePack = z.infer<typeof RulePack>;

/**
 * Mirrors the field names `users.me-export.ts` actually writes
 * (`replacementCategory`, `active` — not `category`/`enabled`), so an
 * exported JSON file round-trips through import unchanged. `.passthrough()`
 * so an unrelated field the export adds later doesn't reject the whole
 * object.
 */
export const ExportedRule = z
  .object({
    name: z.string().min(1).max(80),
    pattern: z.string().min(PATTERN_LIST_MIN_LENGTH).max(300),
    replacementCategory: z.enum(RULE_CATEGORIES),
    active: z.boolean(),
    description: z.string().max(200).nullish(),
  })
  .passthrough();
export type ExportedRule = z.infer<typeof ExportedRule>;

export const ExportedAllowlistEntry = z
  .object({
    value: z.string().trim().min(PATTERN_LIST_MIN_LENGTH).max(200),
    caseSensitive: z.boolean(),
    enabled: z.boolean(),
  })
  .passthrough();
export type ExportedAllowlistEntry = z.infer<typeof ExportedAllowlistEntry>;

export const RulesImportPackRef = z.object({ pack: z.string().min(1).max(40) }).strict();
export type RulesImportPackRef = z.infer<typeof RulesImportPackRef>;

/**
 * Deliberately loose: `rules`/`allowlist` are `z.unknown()[]`, NOT
 * `RulePackRule`/`RulePackAllowlistEntry`. A strict envelope would let one
 * malformed rule in an otherwise-valid file fail the whole import — the
 * opposite of what plan 09 requires (each rule is validated and reported on
 * individually, in the import route, not gate-kept here).
 */
export const RulesImportPackBody = z
  .object({
    format: z.literal(RULE_PACK_FORMAT),
    id: z.string().max(40).optional(),
    rules: z.array(z.unknown()).max(200).optional(),
    allowlist: z.array(z.unknown()).max(200).optional(),
  })
  .passthrough();
export type RulesImportPackBody = z.infer<typeof RulesImportPackBody>;

/** Same looseness rationale as RulesImportPackBody — this is the shape
 * `ExportDataButton`'s JSON download produces, fed straight back in. */
export const RulesImportExportBody = z
  .object({
    custom_redaction_rules: z.array(z.unknown()).max(200).optional(),
    allowlist_entries: z.array(z.unknown()).max(200).optional(),
  })
  .passthrough();
export type RulesImportExportBody = z.infer<typeof RulesImportExportBody>;

export const RulesImportRequest = z.union([
  RulesImportPackRef,
  RulesImportPackBody,
  RulesImportExportBody,
]);
export type RulesImportRequest = z.infer<typeof RulesImportRequest>;

export const RulesImportResult = z.object({
  imported: z.object({ rules: z.number().int(), allowlist: z.number().int() }),
  skippedDuplicates: z.object({ rules: z.number().int(), allowlist: z.number().int() }),
  rejected: z.array(
    z.object({
      list: z.enum(["rules", "allowlist"]),
      index: z.number().int(),
      name: z.string().max(80),
      code: z.string(),
    }),
  ),
  quotaReached: z.boolean(),
  pack: z.string().max(40).nullable(),
});
export type RulesImportResult = z.infer<typeof RulesImportResult>;
